// O limite do Grátis na mesa (item 59.3), só para quem está no Grátis:
// - o chip fixo junto do placar ("2 grátis hoje", "1 grátis hoje" ou "Última grátis"), que nunca cobre cartas, vira,
//   pé ou botões de truco;
// - a faixa de 6 s abaixo do placar no começo da partida, com 1 ou 0 partidas sobrando (item 68): "Partida 1 de 2
//   grátis hoje" na que deixa só 1 e "Última partida grátis de hoje" na última; "bônus" quando ela saiu do bônus;
// - a folha "Jogue sem limite" sobre a mesa, sem sair da partida: ASSINAR AGORA abre o Checkout em outra aba (a mesa
//   continua conectada nesta) e "Chamar um amigo" copia o link de indicação e abre o compartilhar;
// - no fim da partida, com 1 sobrando, o cartão "Só resta 1 partida grátis hoje" no topo (item 68); com 2 ou mais, a
//   linha das grátis que sobram; sem nenhuma, o cartão do limite no lugar da revanche.
import { useContext, useEffect, useState } from 'react';
import { BONUS_POR_INDICACAO, precoEmReais } from '@truco6/planos';
import { avisoDoLimiteVisto, diariasRestantes, noGratis, toqueEmAssinar, toqueEmIndique, useLimite } from '../comum/limite.ts';
import { CartasDoDia } from '../comum/GuiaDoDia.tsx';
import { ir } from '../comum/rotas.ts';
import { PlanoDaCasca } from '../comum/SeloGratis.tsx';
import type { EstadoMesa } from './conexao.ts';

export const FAIXA_DO_LIMITE_MS = 6000;

// Quantas partidas grátis sobram hoje, para quem está no Grátis; null para quem assina (ou antes de saber).
export function useGratisNaMesa(): number | null {
  const plano = useContext(PlanoDaCasca), l = useLimite();
  if (!plano || !l || !noGratis(plano, l) || l.restantes === null) return null;
  return l.restantes;
}

export const textoDoChip = (restantes: number) => (restantes <= 0 ? 'Última grátis' : `${restantes} grátis hoje`);

export function ChipLimite({ restantes, aoAbrir }: { restantes: number; aoAbrir: () => void }) {
  useEffect(() => { avisoDoLimiteVisto('mesa_chip'); }, []);
  return (
    <button className={'chip-limite' + (restantes <= 0 ? ' ultima' : '')} onClick={aoAbrir}
      aria-label={`${textoDoChip(restantes)}. Jogar sem limite`}>
      {textoDoChip(restantes)}
    </button>
  );
}

// A faixa do começo da partida: só quando a mesa avisou agora (limite desta partida) e sobra 1 ou nenhuma.
// A mesa redesenha a cada segundo: passados os 6 s, a faixa some.
export interface FaixaDoLimite { restantes: number; origem: 'diaria' | 'bonus' }
export function useFaixaDoLimite(e: EstadoMesa): FaixaDoLimite | null {
  const l = e.limite;
  const [ate, setAte] = useState(0);
  useEffect(() => { if (l && l.restantes <= 1) setAte(Date.now() + FAIXA_DO_LIMITE_MS); }, [l?.em]);
  return l && l.restantes <= 1 && Date.now() < ate ? { restantes: l.restantes, origem: l.origem } : null;
}

// O texto da faixa. partidaDoDia: quantas das partidas do dia já contaram, esta inclusive (a "1" de "Partida 1 de 2").
export function textoDaFaixa(f: FaixaDoLimite, partidaDoDia: number, limite: number): string {
  const tipo = f.origem === 'bonus' ? 'bônus' : 'grátis', preco = precoEmReais('bronze');
  if (f.restantes <= 0) return `Última partida ${tipo} de hoje. Assine por ${preco} e siga jogando, ou chame um amigo e ganhe +${BONUS_POR_INDICACAO}.`;
  if (f.origem === 'bonus') return 'Partida bônus. Depois desta, sobra só 1.';
  return `Partida ${partidaDoDia} de ${limite} grátis hoje. Depois desta, sobra só 1.`;
}

export function FaixaLimite({ faixa, aoAbrir }: { faixa: FaixaDoLimite; aoAbrir: () => void }) {
  const l = useLimite();
  useEffect(() => { avisoDoLimiteVisto('mesa_faixa'); }, []);
  const limite = l?.limite ?? 2, partidaDoDia = Math.max(1, Math.min(limite, l?.usadas ?? 1));
  return <button className="faixa-limite" role="status" onClick={aoAbrir}>{textoDaFaixa(faixa, partidaDoDia, limite)}</button>;
}

// Copia o link de indicação e abre o compartilhar do aparelho (quando há).
async function chamarAmigo(lerConvite?: () => Promise<string | null>): Promise<string> {
  const link = (await lerConvite?.().catch(() => null)) ?? null;
  if (!link) return 'Abra Indique e ganhe no Perfil para pegar o seu link.';
  try { await navigator.clipboard?.writeText(link); } catch { /* sem permissão: o compartilhar ainda leva o link */ }
  try { await navigator.share?.({ title: 'Truco de 6', text: 'Bora um truco no Truco de 6? Entra pelo meu link:', url: link }); } catch { /* fechou o compartilhar */ }
  return 'Link copiado. Cada amigo novo vale +' + BONUS_POR_INDICACAO + ' partidas para você.';
}

export function FolhaLimite({ aoFechar, assinar, lerConvite }: {
  aoFechar: () => void; assinar?: () => Promise<void>; lerConvite?: () => Promise<string | null>;
}) {
  const [ocupado, setOcupado] = useState(false);
  const [nota, setNota] = useState<string | null>(null);
  return (
    <div className="sheet folha-limite" role="dialog" aria-label="Jogue sem limite">
      <div className="cab-folha">
        <div><h2>Jogue sem limite</h2><span>Bronze, {precoEmReais('bronze')} por mês. Partidas ilimitadas em qualquer mesa.</span></div>
        <button className="fechar-folha" aria-label="Fechar" onClick={aoFechar}>×</button>
      </div>
      <button className="btn1 largo" disabled={ocupado} onClick={async () => {
        toqueEmAssinar('mesa');
        if (!assinar) { ir({ tela: 'planos', plano: 'bronze', ciclo: 'mensal' }); return; }
        setOcupado(true);
        try { await assinar(); setNota('O pagamento abriu em outra aba. Sua mesa continua aqui.'); } catch (err) { setNota((err as Error).message); }
        setOcupado(false);
      }}>{ocupado ? 'UM INSTANTE...' : 'ASSINAR AGORA'}</button>
      <button className="btn2 largo" onClick={async () => { toqueEmIndique('mesa'); setNota(await chamarAmigo(lerConvite)); }}>
        Chamar um amigo (+{BONUS_POR_INDICACAO} partidas)
      </button>
      {nota && <p className="nota-folha" role="status">{nota}</p>}
      <button className="link" onClick={aoFechar}>Depois da partida</button>
    </div>
  );
}

// Fim da partida (canvas 1.4b): com 1 sobrando, o cartão "Só resta 1 partida grátis hoje" no topo da coluna da próxima
// ação, com ASSINAR (Checkout do Bronze em outra aba) e CHAMAR AMIGO, os dois secundários (o laranja fica com a
// revanche); com 2 ou mais, a linha e o Chamar um amigo; sem nenhuma, o cartão do limite.
export function LimiteNoFim({ restantes, lerConvite, assinar }: {
  restantes: number; lerConvite?: () => Promise<string | null>; assinar?: () => Promise<void>;
}) {
  if (restantes === 1) return <RestaUmaNoFim lerConvite={lerConvite} assinar={assinar} />;
  return <LinhaOuLimiteNoFim restantes={restantes} lerConvite={lerConvite} assinar={assinar} />;
}

function RestaUmaNoFim({ lerConvite, assinar }: { lerConvite?: () => Promise<string | null>; assinar?: () => Promise<void> }) {
  const l = useLimite();
  const [nota, setNota] = useState<string | null>(null);
  useEffect(() => { avisoDoLimiteVisto('fim_resta_uma'); }, []);
  // A que sobra é do bônus quando as do dia já acabaram.
  const bonus = !!l && l.limite !== null && diariasRestantes(l) === 0;
  const total = l?.limite ?? 2, jogadas = Math.max(0, total - 1);
  const preco = precoEmReais('bronze').replace(' ', '\u00a0'); // "R$ 1,99" nunca quebra a linha
  return (
    <section className="resta-uma" role="status" aria-label={bonus ? 'Só resta 1 partida bônus' : 'Só resta 1 partida grátis hoje'}>
      <div className="cab-resta-uma">
        <CartasDoDia total={total} jogadas={jogadas} destaque rotulo={`${jogadas} ${jogadas === 1 ? 'partida jogada' : 'partidas jogadas'}, 1 sobrando`} />
        <div className="titulo-resta-uma">
          <span className="rotulo-guia">{bonus ? 'BÔNUS DOS AMIGOS' : `${jogadas} DE ${total} JOGADAS HOJE`}</span>
          <h2>{bonus ? 'Só resta 1 partida bônus' : 'Só resta 1 partida grátis hoje'}</h2>
        </div>
      </div>
      <p>Depois dela, só amanhã. Assine o Bronze por {preco} e jogue sem limite.</p>
      <div className="botoes-resta-uma">
        <button className="assinar-resta" onClick={() => {
          toqueEmAssinar('fim_partida');
          if (assinar) assinar().then(() => setNota('O pagamento abriu em outra aba.'), (err: Error) => setNota(err.message)); else ir({ tela: 'planos', plano: 'bronze', ciclo: 'mensal' });
        }}><b>ASSINAR</b><small>{preco} por mês</small></button>
        <button className="amigo-resta" onClick={async () => { toqueEmIndique('fim_partida'); setNota(await chamarAmigo(lerConvite)); }}>
          <b>CHAMAR AMIGO</b><small>+{BONUS_POR_INDICACAO} partidas</small>
        </button>
      </div>
      {nota && <small className="nota-resta" role="status">{nota}</small>}
    </section>
  );
}

function LinhaOuLimiteNoFim({ restantes, lerConvite, assinar }: {
  restantes: number; lerConvite?: () => Promise<string | null>; assinar?: () => Promise<void>;
}) {
  const [nota, setNota] = useState<string | null>(null);
  useEffect(() => { avisoDoLimiteVisto('fim_partida'); }, []);
  const amigo = (
    <button className="btn2" onClick={async () => { toqueEmIndique('fim_partida'); setNota(await chamarAmigo(lerConvite)); }}>
      Chamar um amigo (+{BONUS_POR_INDICACAO})
    </button>
  );
  if (restantes > 0) {
    return (
      <section className="limite-no-fim" aria-label="Partidas grátis de hoje">
        <p>Você ainda tem <b>{restantes}</b> grátis hoje</p>
        {amigo}
        {nota && <small role="status">{nota}</small>}
      </section>
    );
  }
  return (
    <section className="limite-no-fim sem" aria-label="Sem partidas grátis hoje">
      <b>Acabaram suas partidas grátis de hoje</b>
      <small>Elas voltam à meia-noite. Assine o Bronze por {precoEmReais('bronze')} por mês e jogue sem limite.</small>
      <button className="btn1 largo" onClick={() => {
        toqueEmAssinar('fim_partida');
        if (assinar) assinar().catch((err: Error) => setNota(err.message)); else ir({ tela: 'limite' });
      }}>ASSINAR BRONZE</button>
      {amigo}
      {nota && <small role="status">{nota}</small>}
    </section>
  );
}
